import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  useTheme,
  useMediaQuery,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
} from '@mui/material';
import {
  Save,
  Refresh,
  Visibility,
  Article,
  Close,
  Fullscreen,
  FullscreenExit,
  PictureAsPdf,
} from '@mui/icons-material';
import { Contract } from '../../types';
import templateService, { Template } from '../../services/templateService';
import { contractService } from '../../services/contractService';
import { api } from '../../services/api';
import SysEditor from '../syseditor/SysEditor';



interface ContractLayoutProps {
  contractId: number;
  contract?: Contract;
  contractStatus: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura' | 'suspenso';
}

const ContractLayout: React.FC<ContractLayoutProps> = ({ contractId, contract, contractStatus }) => {
  const [templateContent, setTemplateContent] = useState<string>('');
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadContractTemplate();
    loadAvailableTemplates();
  }, [contractId, contract]);

  // Recarregar template quando o componente é remontado (navegação)
  useEffect(() => {
    // Pequeno delay para garantir que o componente está totalmente montado
    const timer = setTimeout(() => {
      loadContractTemplate();
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Array vazio = executa apenas na montagem

  const loadContractTemplate = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar o contrato atualizado do backend
      const response = await contractService.getById(contractId);

      if (response.success && response.data) {
        const contractData = response.data;

        // Carregar o template se existir
        if (contractData.template) {
          setTemplateContent(contractData.template);
        } else {
          setTemplateContent('');
        }
      } else {
        // Fallback para o contract prop se a busca falhar
        if (contract?.template) {
          setTemplateContent(contract.template);
        } else {
          setTemplateContent('');
        }
      }

    } catch (err: any) {
      console.error('Erro ao carregar template do contrato:', err);

      // Fallback para o contract prop em caso de erro
      if (contract?.template) {
        setTemplateContent(contract.template);
      } else {
        setTemplateContent('');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTemplates = async () => {
    try {
      setTemplatesLoading(true);
      setError(null);

      const response = await templateService.getTemplates({
        type: 'contrato',
        isActive: true,
        limit: 50
      });

      setAvailableTemplates(response.templates || []);

    } catch (err: any) {
      setError('Erro ao carregar templates disponíveis: ' + (err.message || 'Erro desconhecido'));
      setAvailableTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleTemplateSelect = async (templateId: number) => {
    if (!templateId) return;

    try {
      setLoading(true);
      setError(null);

      const template = await templateService.getTemplateById(templateId);

      setTemplateContent(template.content);
      setSelectedTemplateId(''); // Limpar seleção após copiar

      setSuccess(`Template "${template.title}" copiado com sucesso!`);
      setTimeout(() => setSuccess(null), 3000);

    } catch (err: any) {
      setError('Erro ao carregar template: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (currentContent?: string) => {
    try {
      setSaving(true);
      setError(null);

      // Usar o conteúdo atual passado como parâmetro ou o estado atual
      const contentToSave = currentContent || templateContent;

      const response = await contractService.update(contractId, {
        template: contentToSave
      });

      if (response.success) {
        setSuccess('Template salvo com sucesso!');
        setTimeout(() => setSuccess(null), 3000);

        // Atualizar o estado local com o conteúdo salvo
        if (currentContent && currentContent !== templateContent) {
          setTemplateContent(currentContent);
        }
      } else {
        throw new Error(response.message || 'Erro ao salvar template');
      }

    } catch (err: any) {
      setError('Erro ao salvar template: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!templateContent.trim()) {
      setError('Não há conteúdo para visualizar');
      return;
    }

    try {
      setPreviewLoading(true);
      setError(null);

      const response = await contractService.getContractContent(contractId);
      
        setPreviewContent(response.data || '<h1>Contrato sem template definido.</h1>');
      
    } catch (err: any) {
      console.error('Erro ao buscar conteúdo do contrato:', err);      
    } finally {
      setPreviewLoading(false);
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setFullscreen(false);
    setPreviewContent('');
  };

  const handleToggleFullscreen = () => {
    setFullscreen(!fullscreen);
  };

  const handleDownloadPdf = async () => {
    if (!previewContent) return;

    try {
      setPdfLoading(true);
      const filename = `contrato-${contract?.titulo || contractId}.pdf`;

      const response = await api.post('/pdf/generate', {
        html: previewContent,
        filename,
      }, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Erro ao gerar PDF:', err);
      setError('Erro ao gerar PDF: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setPdfLoading(false);
    }
  };

  const renderPreviewContent = () => {
    if (previewLoading) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Carregando preview do contrato...
          </Typography>
        </Box>
      );
    }

    if (!previewContent) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          color="text.secondary"
        >
          <Article sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" gutterBottom>
            Nenhum conteúdo para visualizar
          </Typography>
          <Typography variant="body2">
            Digite ou cole um template no editor para visualizar
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          '& h1, & h2, & h3': {
            color: theme.palette.primary.main,
            marginTop: 0,
            marginBottom: theme.spacing(2)
          },
          '& p': {
            marginBottom: theme.spacing(1),
            lineHeight: 1.6
          },
          '& strong': {
            fontWeight: 600
          },
          '& ul, & ol': {
            paddingLeft: theme.spacing(3)
          },
          '& li': {
            marginBottom: theme.spacing(0.5)
          }
        }}
        dangerouslySetInnerHTML={{ __html: previewContent }}
      />
    );
  };





  return (
    <Box>
      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={1}>
        {/* Controles */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="between" mb={1}>
                <Typography variant="h6" fontWeight="bold">
                  Template do Contrato
                </Typography>
                <Box display="flex" gap={1}>
                  <Tooltip title="Recarregar templates">
                    <IconButton size="small" onClick={loadAvailableTemplates} disabled={templatesLoading}>
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Copiar de Template Existente</InputLabel>
                    <Select
                      size="small"
                      value={selectedTemplateId}
                      label="Copiar de Template Existente"
                      onChange={(e) => {
                        const templateId = e.target.value as number;
                        setSelectedTemplateId(templateId);
                        if (templateId) {
                          handleTemplateSelect(templateId);
                        }
                      }}
                      disabled={templatesLoading || loading}
                    >
                      <MenuItem value="">
                        <em>Selecione um template para copiar</em>
                      </MenuItem>
                      {availableTemplates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Article fontSize="small" />
                            <Box>
                              <Typography variant="body2">{template.title}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Atualizado em {new Date(template.updatedAt || '').toLocaleDateString('pt-BR')}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                      onClick={() => handleSaveTemplate()}
                      disabled={saving || contractStatus === 'cancelado'}
                    >
                      Salvar Template
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={previewLoading ? <CircularProgress size={16} /> : <Visibility />}
                      onClick={handlePreview}
                      disabled={!templateContent.trim() || contractStatus === 'cancelado' || previewLoading}
                    >
                      {previewLoading ? 'Carregando...' : 'Visualizar'}
                    </Button>

                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Editor de Template */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <SysEditor
                key={`contract-template-${contractId}`}
                value={templateContent}
                onChange={setTemplateContent}
                placeholder="Digite ou cole o conteúdo do template aqui..."
                disabled={saving}
                height={500}
                showVariables={true}
                variablesType="contrato"
                onSave={handleSaveTemplate}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modal de Preview */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth={fullscreen ? false : "md"}
        fullWidth
        fullScreen={fullscreen}
        PaperProps={{
          sx: {
            p: 0,
            scrollbarWidth: 'none',
            height: fullscreen ? '100vh' : '90vh',
            maxHeight: fullscreen ? 'none' : '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            pb: 0.5
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Article color="primary" />
            <Box>
              <Typography variant="h6" component="div">
                Preview do Contrato
              </Typography>
              <Typography variant="caption" color="text.secondary">{contract?.titulo || `Contrato #${contractId}`}</Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Download PDF">
              <IconButton onClick={handleDownloadPdf} disabled={pdfLoading || !previewContent}>
                {pdfLoading ? <CircularProgress size={20} /> : <PictureAsPdf />}
              </IconButton>
            </Tooltip>

            <Tooltip title={fullscreen ? "Sair da tela cheia" : "Tela cheia"}>
              <IconButton onClick={handleToggleFullscreen} disabled={contractStatus === 'cancelado'}>
                {fullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Fechar">
              <IconButton onClick={handleClosePreview}>
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            overflow: 'auto',
            bgcolor: '#f5f5f5',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 0.5,
              minHeight: '100%',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                width: '100%',
                maxWidth: '210mm', // Tamanho A4
                minHeight: fullscreen ? 'calc(100vh - 200px)' : '297mm',
                p: 4,
                bgcolor: 'white',
                color: 'black',
                fontSize: '14px',
                lineHeight: 1.6,
                fontFamily: 'Arial, sans-serif',
                boxShadow: theme.shadows[8],
                '& *': {
                  maxWidth: '100%',
                  wordWrap: 'break-word',
                }
              }}
            >
              {renderPreviewContent()}
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            px: 1,
            py: 1
          }}
        >
          <Button onClick={handleClosePreview}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractLayout; 